import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { callApi, validateToken } from "../../lib/utils";
import moment from "moment";
import axios from "axios";
import Payment from "payment";
import { styles } from "./style";
import { css } from "aphrodite";
import CustomLoader from "../GenericComponents/CustomLoader";
import Footer from "../GenericComponents/Footer";
import "../AccountPage.css";
import { Button, Card, Table, Modal, Form, Row, Col, Badge, Alert } from "react-bootstrap";
import countryCodes from "../iso_country_codes.json";

const initialFormState = {
    nickname: '', cardHolderName: '', cardNumber: '', cardType: "", cvc: '',
    expiryMonth: '', expiryYear: "", address: "", localityaddress: "",
    zipCode: "", city: "", state: "", country: "", setAsDefault: true
};

const formatCardTitle = (method) =>
    `${(method.cardType || "").toUpperCase()} •••• ${method.last4}`;

const formatCardExpiry = (month, year) => {
    const mm = String(month || "").padStart(2, "0");
    const yy = String(year || "").slice(-2);
    return `${mm}/${yy}`;
};

const formatBillingAddress = (method) => {
    const countryName =
        countryCodes.find((country) => country.code === method.country)
            ?.countryName || method.country;

    return [
        method.addressLine1,
        method.addressLine2,
        method.city,
        method.state,
        method.zipcode || method.zipCode,
        countryName,
    ]
        .filter(Boolean)
        .join(", ");
};


const ManagePayments = (props) => {
    const [transactions, setTransactions] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageAlert, setPageAlert] = useState({ show: false, message: '', variant: 'danger' });
    const [showAddCardModal, setShowAddCardModal] = useState(false);
    const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
    const [deletingPaymentMethod, setDeletingPaymentMethod] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    // State for the "Add / Edit Card" form
    const [formInputs, setFormInputs] = useState(initialFormState);
    const [isFormValid, setIsFormValid] = useState(false);
    const [modalAlert, setModalAlert] = useState({ show: false, message: '', variant: 'danger' });
    const [showDefaultWarningModal, setShowDefaultWarningModal] = useState(false);
    const isReadOnly = props.user?.roleId === 10;
    const isEditMode = !!editingPaymentMethod;


    // --- Card Formatting and Validation Logic ---
    const clearNumber = (value = "") => value.replace(/\D+/g, "");

    const formatCreditCardNumber = (value) => {
        if (!value) return value;
        const issuer = Payment.fns.cardType(clearNumber(value));
        const maxDigits = issuer === "amex" ? 15 : issuer === "dinersclub" ? 14 : 16;
        const clearValue = clearNumber(value).slice(0, maxDigits);
        let nextValue;
        switch (issuer) {
            case "amex":
                nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
                    4,
                    10
                )} ${clearValue.slice(10, 15)}`;
                break;
            case "dinersclub":
                nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
                    4,
                    10
                )} ${clearValue.slice(10, 14)}`;
                break;
            default:
                nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
                    4,
                    8
                )} ${clearValue.slice(8, 12)} ${clearValue.slice(12, 16)}`;
                break;
        }
        setFormInputs(prev => ({ ...prev, cardType: (issuer || '').charAt(0).toUpperCase() + (issuer || '').slice(1) }));
        return nextValue.trim();
    };

    const formatCVC = (value) => {
        const clearValue = clearNumber(value).replace(/^0+/, "");
        const maxLength = formInputs.cardType === "Amex" ? 4 : 3;
        return clearValue.slice(0, maxLength);
    };

    const handleFormChange = (e) => {
        let { name, value } = e.target;
        if (name === 'cardNumber') value = formatCreditCardNumber(value);
        if (name === 'cvc') value = formatCVC(value);
        if (name === 'expiryMonth') value = clearNumber(value).slice(0, 2);
        if (name === 'expiryYear') value = clearNumber(value).slice(0, 4);
        if (name === "city" || name === "state") value = value.replace(/[0-9]/g, "");
        if (name === "zipCode") value = value.replace(/[^0-9-]/g, "").replace(/^-+/, "");
        setFormInputs(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        // Live validation for the Add / Edit Card form
        const errors = {};
        const { cardHolderName, cardNumber, expiryMonth, expiryYear, cvc, address, zipCode, city, state, country } = formInputs;
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;

        if (expiryYear && expiryMonth && (parseInt(expiryYear) < currentYear || (parseInt(expiryYear) === currentYear && parseInt(expiryMonth) < currentMonth))) {
            errors.expiry = "Expiry date cannot be in the past.";
        } else if (expiryMonth && (parseInt(expiryMonth) < 1 || parseInt(expiryMonth) > 12)) {
            errors.expiry = "Month must be between 01 and 12.";
        }

        setFormErrors(errors);

        const requiredFieldsFilled = isEditMode
            ? cardHolderName && expiryMonth && expiryYear && address && zipCode && city && state && country
            : cardHolderName && cardNumber && expiryMonth && expiryYear && cvc && address && zipCode && city && state && country;
        setIsFormValid(requiredFieldsFilled && Object.keys(errors).length === 0);
    }, [formInputs, isEditMode]);


    const fetchData = async () => {
        setLoading(true);
        setPageAlert({ show: false });
        try {
            const [methodsResponse, transactionsResponse] = await Promise.all([
                callApi(`/api/payment-methods`, {}, "GET"),
                callApi(`/api/orders/payment-transactions/list`, { start: null, end: null, timezone: moment.tz.guess(), page: 1, limit: 20 }, "POST")
            ]);
            if (methodsResponse.data.success) setPaymentMethods(methodsResponse.data.data.paymentMethods || []);
            if (transactionsResponse.data.success) setTransactions(transactionsResponse.data.data.paymentTransactions || []);
        } catch (err) {
            setPageAlert({ show: true, message: "Failed to load payment details.", variant: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        validateToken((err) => {
            if (err) window.location = "/logout";
            else fetchData();
        });
    }, [props.profileData.id]);

    const getCardIcon = () => (
        <i className="bi bi-credit-card payment-method-card-icon-symbol" aria-hidden="true" />
    );

    // const handleDownloadInvoice = async (invoiceId) => {
    //     setLoading(true);
    //     setPageAlert({ show: false }); // Clear previous alerts
    //     try {
    //         const response = await axios({
    //             url: `/api/orders/payment-transaction/invoice/export`,
    //             method: 'GET',
    //             responseType: 'blob', // Crucial for file downloads
    //         });

    //         // Create a temporary URL to the blob and trigger download
    //         const url = window.URL.createObjectURL(new Blob([response.data]));
    //         const link = document.createElement('a');
    //         link.href = url;
    //         link.setAttribute('download', `invoice-${invoiceId}.pdf`);
    //         document.body.appendChild(link);
    //         link.click();

    //         // Clean up the temporary URL and link
    //         window.URL.revokeObjectURL(url);
    //         link.parentNode.removeChild(link);
    //     } catch (err) {
    //         setPageAlert({
    //             show: true,
    //             variant: 'danger',
    //             message: 'Failed to download invoice. Please try again later.'
    //         });
    //         console.error("Invoice download failed:", err);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleDownloadInvoice = (data) => {
        let params = {
            //start: startDate,
            //end: endDate,
            stripeInvoiceId: data.invoiceId,
            timezone: moment.tz.guess(),
        };
        setLoading(true);

        axios({
            method: "post",
            url: `/api/orders/payment-transaction/invoice/export`,
            responseType: "blob",
            data: params,
        })
            .then((response) => {
                var filename = "";
                let disposition = null;

                if (Object.keys(response.headers).includes("content-disposition")) {
                    disposition = response.headers["content-disposition"];
                } else if (
                    Object.keys(response.headers).includes("Content-Disposition")
                ) {
                    disposition = response.headers["Content-Disposition"];
                }

                // var disposition = xhr.getResponseHeader('Content-Disposition');
                if (disposition && disposition.indexOf("attachment") !== -1) {
                    var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    var matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) {
                        filename = matches[1].replace(/['"]/g, "");
                    }
                }
                let blobData = response.data;
                setLoading(false);
                fileDownload(filename, blobData, "pdf");
            })
            .catch((err) => {
                console.log("err: ", err);
                setLoading(false);
            });
    };

    const fileDownload = (filename, blobData, type) => {
        var blobURL = window.URL.createObjectURL(blobData);
        var tempLink = document.createElement("a");
        tempLink.style.display = "none";
        tempLink.href = blobURL;
        tempLink.setAttribute(
            "download",
            filename !== ""
                ? `${filename}`
                : type === "xlsx"
                    ? "report.xlsx"
                    : type === "csv" ? "report.csv" : "report.pdf"
        );
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        window.URL.revokeObjectURL(blobURL);
    };

    const handleAddNewCard = () => {
        setLoading(true);
        setModalAlert({ show: false });
        const params = {
            cardType: formInputs.cardType,
            cardNumber: formInputs.cardNumber.replace(/\s/g, ''),
            expiryMonth: parseInt(formInputs.expiryMonth),
            expiryYear: parseInt(formInputs.expiryYear),
            cvc: formInputs.cvc,
            nickname: formInputs.nickname.trim(),
            customerName: formInputs.cardHolderName,
            addressLine1: formInputs.address,
            addressLine2: formInputs.localityaddress,
            city: formInputs.city,
            state: formInputs.state,
            country: formInputs.country,
            zipcode: formInputs.zipCode,
            phoneNumber: null,
            setAsDefault: formInputs.setAsDefault
        };

        callApi(`/api/payment-methods`, params, "POST")
            .then(() => {
                setModalAlert({ show: true, message: "New payment method added successfully!", variant: 'success' });
                setTimeout(() => {
                    handleCloseModal();
                    fetchData();
                }, 2000); // Close modal after 2 seconds
            })
            .catch((err) => setModalAlert({ show: true, message: err.message || "Failed to add new payment method.", variant: 'danger' }))
            .finally(() => setLoading(false));
    };

    const handleUpdateCard = () => {
        if (!editingPaymentMethod?.id) return;

        setLoading(true);
        setModalAlert({ show: false });
        const params = {
            expiryMonth: parseInt(formInputs.expiryMonth),
            expiryYear: parseInt(formInputs.expiryYear),
            nickname: formInputs.nickname.trim(),
            customerName: formInputs.cardHolderName,
            addressLine1: formInputs.address,
            addressLine2: formInputs.localityaddress || null,
            city: formInputs.city,
            state: formInputs.state,
            country: formInputs.country,
            zipcode: formInputs.zipCode,
            phoneNumber: null,
            setAsDefault: formInputs.setAsDefault
        };

        callApi(`/api/payment-methods/${editingPaymentMethod.id}`, params, "PATCH")
            .then(() => {
                setModalAlert({ show: true, message: "Payment method updated successfully!", variant: 'success' });
                setTimeout(() => {
                    handleCloseModal();
                    fetchData();
                }, 1500);
            })
            .catch((err) => setModalAlert({ show: true, message: err.message || "Failed to update payment method.", variant: 'danger' }))
            .finally(() => setLoading(false));
    };

    const handleDeleteCard = () => {
        if (!deletingPaymentMethod?.id) return;

        setLoading(true);
        setPageAlert({ show: false });
        callApi(`/api/payment-methods/${deletingPaymentMethod.id}`, {}, "DELETE")
            .then(async () => {
                setDeletingPaymentMethod(null);
                await fetchData();
                setPageAlert({ show: true, message: "Payment method deleted successfully.", variant: 'success' });
            })
            .catch((err) => {
                setPageAlert({ show: true, message: err.message || "Failed to delete payment method.", variant: 'danger' });
                setLoading(false);
            });
    };

    const handleDefaultSwitchChange = (e) => {
        const nextChecked = e.target.checked;
        if (!nextChecked) {
            setFormInputs(prev => ({ ...prev, setAsDefault: false }));
            setShowDefaultWarningModal(true);
            return;
        }

        setFormInputs(prev => ({ ...prev, setAsDefault: true }));
    };

    // Reset modal alert when it opens
    const openAddCardModal = () => {
        setEditingPaymentMethod(null);
        setFormInputs(initialFormState);
        setFormErrors({});
        setModalAlert({ show: false });
        setShowAddCardModal(true);
    };

    const openEditCardModal = (method) => {
        setEditingPaymentMethod(method);
        setFormInputs({
            nickname: method.nickname || '',
            cardHolderName: method.customerName || '',
            cardNumber: method.last4 ? `•••• •••• •••• ${method.last4}` : '',
            cardType: method.cardType
                ? method.cardType.charAt(0).toUpperCase() + method.cardType.slice(1).toLowerCase()
                : '',
            cvc: '',
            expiryMonth: method.expiryMonth ? String(method.expiryMonth).padStart(2, '0') : '',
            expiryYear: method.expiryYear ? String(method.expiryYear) : '',
            address: method.addressLine1 || '',
            localityaddress: method.addressLine2 || '',
            zipCode: method.zipcode || method.zipCode || '',
            city: method.city || '',
            state: method.state || '',
            country: method.country || '',
            setAsDefault: method.status === 'ACTIVE'
        });
        setFormErrors({});
        setModalAlert({ show: false });
        setShowAddCardModal(true);
    };

    const handleCloseModal = () => {
        setShowAddCardModal(false);
        setEditingPaymentMethod(null);
        setFormInputs(initialFormState); // Reset form fields
        setFormErrors({}); // Reset validation errors
        setShowDefaultWarningModal(false);
        setModalAlert({ show: false });
    };

    const StatusBadge = ({ status }) => {
        const statusClass = status === 'succeeded' ? 'bg-success text-white' : 'bg-danger text-white';
        const iconClass = status === 'succeeded' ? 'bi-check-circle-fill' : 'bi-x-circle-fill';
        return (
            <span className={`badge rounded-pill fs-6 ${statusClass}`}>
                <i className={`bi ${iconClass} me-2`}></i>
                <span className="text-capitalize">{status}</span>
            </span>
        );
    };

    return (
        <div className="account-page">
            <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet" />
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css" />
            <CustomLoader loading={loading} />
            <div className={css(styles.mainGrid)}>
                <div className={css(styles.header)}>{"Payments"}</div>

                {pageAlert.show && <Alert variant={pageAlert.variant} onClose={() => setPageAlert({ show: false })} dismissible>{pageAlert.message}</Alert>}

                <div className="payments-section-header mb-2 mt-4">
                    <h4 className="mb-0 text-muted">Payment Methods</h4>
                    {!isReadOnly &&
                        <Button variant="outline-primary" size="sm" onClick={openAddCardModal}>
                            <i className="bi bi-plus-circle me-1"></i> Add New Card
                        </Button>}
                </div>

                {paymentMethods.length > 0 ? [...paymentMethods]
                    .sort((a, b) => (a.status === 'ACTIVE' ? -1 : b.status === 'ACTIVE' ? 1 : 0))
                    .map(method => {
                        const billingAddress = formatBillingAddress(method);
                        const nickname = method.nickname?.trim();

                        return (
                        <Card key={method.id} className={`shadow-sm mb-3 payment-method-card ${method.isDefault ? 'border-primary' : ''}`}>
                            <Card.Body className="payment-method-card-body">
                                <div className="payment-method-card-icon">
                                    {getCardIcon()}
                                </div>
                                <div className="payment-method-card-content">
                                    <div className="payment-method-card-title-row">
                                        <span className="payment-method-card-title">
                                            {formatCardTitle(method)}
                                        </span>
                                        {method.isDefault ? (
                                            <Badge pill bg="primary" className="payment-method-default-badge">
                                                Default
                                            </Badge>
                                        ) : null}
                                    </div>
                                    {method.customerName ? (
                                        <div className="payment-method-card-line">
                                            {method.customerName}
                                        </div>
                                    ) : null}
                                    <div className="payment-method-card-line">
                                        {`Expires ${formatCardExpiry(method.expiryMonth, method.expiryYear)}`}
                                    </div>
                                    {billingAddress ? (
                                        <div className="payment-method-card-line">
                                            <span className="fw-bold">Billing Address:</span>{" "}
                                            {billingAddress}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="payment-method-card-actions">
                                    {nickname ? (
                                        <span className="payment-method-nickname-badge">
                                            {nickname}
                                        </span>
                                    ) : null}
                                    {!isReadOnly && (
                                        <div className="payment-method-action-buttons">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="payment-method-action-btn"
                                                title="Edit card"
                                                aria-label="Edit card"
                                                onClick={() => openEditCardModal(method)}
                                            >
                                                <i className="bi bi-pencil-square"></i>
                                            </Button>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="payment-method-action-btn text-danger"
                                                title={paymentMethods.length === 1 ? "At least one card is required" : "Delete card"}
                                                aria-label="Delete card"
                                                disabled={paymentMethods.length === 1}
                                                onClick={() => setDeletingPaymentMethod(method)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                        );
                    }) : (
                    !loading && <Card className="shadow-sm mb-5"><Card.Body><p className="text-muted mb-0">No payment methods on file.</p></Card.Body></Card>
                )}

                <h4 className="mb-3 mt-5 text-muted">Payment Transactions</h4>
                {transactions.length > 0 ? (
                    <div className="card payments-table-card">
                        <div className="table-responsive payments-table-wrapper">
                        <Table hover className="mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-3 text-start">TRANSACTION ID</th>
                                    <th className="text-start">PAID ON</th>
                                    <th className="text-start">DESCRIPTION</th>
                                    <th className="text-start">AMOUNT</th>
                                    <th className="text-start">STATUS</th>
                                    <th className="pe-3 text-start">INVOICE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((tx) => (
                                    <tr key={tx.transactionId}>
                                        <td className="ps-3 text-muted small text-start">{tx.transactionId}</td>
                                        <td className="text-muted text-start">{moment(tx.paymentDate).format("MM/DD/YYYY")}</td>
                                        <td className=" text-muted text-start">{`${tx.paymentMethod.card.brand} **** ${tx.paymentMethod.card.last4}`}</td>
                                        <td className="fw-bold text-start">{`$${tx.amount.amount_received.toFixed(2)}`}</td>
                                        <td className="text-start"><StatusBadge status={tx.status} /></td>
                                        <td className="pe-3 text-start">{tx.invoiceId ? (<Button variant="link" size="sm" onClick={() => handleDownloadInvoice(tx)} title="Download Invoice"><i className="bi bi-download"></i></Button>) : <span>{"N/A"}</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        </div>
                    </div>
                ) : (
                    !loading && <div className="text-center text-muted p-4 border rounded"><p className="mb-0">No payment transactions found.</p></div>
                )}

                <Modal show={showAddCardModal} onHide={handleCloseModal} centered size="lg" fullscreen="md-down">
                    <Modal.Header closeButton>
                        <Modal.Title>{isEditMode ? "Edit Card" : "Add a New Card"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalAlert.show && (
                            <Alert
                                variant={modalAlert.variant}
                                onClose={() => setModalAlert({ show: false })}
                                dismissible
                            >
                                {modalAlert.message}
                            </Alert>
                        )}
                        {isEditMode && (
                            <Alert variant="info" className="mb-3">
                                Card number, CVC, and card type cannot be changed. To update those, add a new card and remove this one.
                            </Alert>
                        )}
                        <Form noValidate>
                            <Form.Group className="mb-3">
                                <Form.Label>Card Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nickname"
                                    value={formInputs.nickname}
                                    onChange={handleFormChange}
                                    placeholder="Enter a name for this card"
                                />
                                <Form.Text className="text-muted">
                                    e.g., Sales Card, Personal Card
                                </Form.Text>
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Card Number {!isEditMode && <span className="text-danger">*</span>}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cardNumber"
                                            value={formInputs.cardNumber}
                                            onChange={handleFormChange}
                                            required={!isEditMode}
                                            readOnly={isEditMode}
                                            disabled={isEditMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Name on Card <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cardHolderName"
                                            value={formInputs.cardHolderName}
                                            onChange={handleFormChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type of Card</Form.Label>
                                        <Form.Control type="text" name="cardType" value={formInputs.cardType} readOnly disabled />
                                    </Form.Group>
                                </Col>
                                <Col xs={4} md={2}>
                                    <Form.Group>
                                        <Form.Label>Expiry Month <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="expiryMonth"
                                            value={formInputs.expiryMonth}
                                            onChange={handleFormChange}
                                            placeholder="MM"
                                            maxLength="2"
                                            required
                                            isInvalid={!!formErrors.expiry}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={4} md={2}>
                                    <Form.Group>
                                        <Form.Label>Expiry Year <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="expiryYear"
                                            value={formInputs.expiryYear}
                                            onChange={handleFormChange}
                                            placeholder="YYYY"
                                            maxLength="4"
                                            required
                                            isInvalid={!!formErrors.expiry}
                                        />
                                    </Form.Group>
                                </Col>
                                {!isEditMode && (
                                    <Col xs={4} md={2}>
                                        <Form.Group>
                                            <Form.Label>CVC <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cvc"
                                                value={formInputs.cvc}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                )}
                                {formErrors.expiry && (
                                    <Col xs={12}>
                                        <Form.Control.Feedback type="invalid" className="d-block">
                                            {formErrors.expiry}
                                        </Form.Control.Feedback>
                                    </Col>
                                )}
                            </Row>
                            <hr className="my-4" />
                            <h5 className="mb-3">Billing Address</h5>
                            <Form.Group className="mb-3"><Form.Label>Address <span className="text-danger">*</span></Form.Label><Form.Control type="text" name="address" value={formInputs.address} onChange={handleFormChange} required /></Form.Group>
                            <Form.Group className="mb-3"><Form.Label>Address Line 2 (Optional)</Form.Label><Form.Control type="text" name="localityaddress" value={formInputs.localityaddress} onChange={handleFormChange} /></Form.Group>
                            <Row>
                                <Col md={6}><Form.Group className="mb-3"><Form.Label>City <span className="text-danger">*</span></Form.Label><Form.Control type="text" name="city" value={formInputs.city} onChange={handleFormChange} maxLength={50} required /></Form.Group></Col>
                                <Col md={6}><Form.Group className="mb-3"><Form.Label>State <span className="text-danger">*</span></Form.Label><Form.Control type="text" name="state" value={formInputs.state} onChange={handleFormChange} maxLength={50}  required /></Form.Group></Col>
                            </Row>
                            <Row>
                                <Col md={6}><Form.Group className="mb-3"><Form.Label>Zip Code <span className="text-danger">*</span></Form.Label><Form.Control type="text" name="zipCode" value={formInputs.zipCode} onChange={handleFormChange} maxLength={20} required /></Form.Group></Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                                        <Form.Select name="country" value={formInputs.country} onChange={handleFormChange} required>
                                            <option value="">Select a country...</option>
                                            {countryCodes.map(country => (
                                                <option key={country.code} value={country.code}>{country.countryName}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Check
                                type="switch"
                                id="default-switch"
                                label="Set as default payment method"
                                checked={formInputs.setAsDefault}
                                onChange={handleDefaultSwitchChange}
                                style={{fontWeight:'600', fontSize:'1.1rem'}}
                            />
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button
                            variant="primary"
                            onClick={isEditMode ? handleUpdateCard : handleAddNewCard}
                            disabled={!isFormValid || loading}
                        >
                            {loading ? 'Saving...' : (isEditMode ? 'Update Card' : 'Save Card')}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal
                    show={!!deletingPaymentMethod}
                    onHide={() => setDeletingPaymentMethod(null)}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Card</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {deletingPaymentMethod ? (
                            <>
                                Are you sure you want to delete{" "}
                                <b>{formatCardTitle(deletingPaymentMethod)}</b>
                                {deletingPaymentMethod.nickname?.trim()
                                    ? ` (${deletingPaymentMethod.nickname.trim()})`
                                    : ""}
                                ?
                            </>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setDeletingPaymentMethod(null)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteCard} disabled={loading}>
                            {loading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal
                    show={showDefaultWarningModal}
                    onHide={() => setShowDefaultWarningModal(false)}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Default payment method</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        This card will no longer be your default. Please select another card to set as your default payment method.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="primary"
                            onClick={() => setShowDefaultWarningModal(false)}
                        >
                            OK
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
            <Footer />
        </div>
    );
};

const mapStateToProps = (store) => ({
    profileData: store.dashboard.profileData,
    user: store.user
});

export default withRouter(connect(mapStateToProps)(ManagePayments));